import { useEffect, useState } from 'react';
import { CanonicalModal, StatusBadge } from '@urbanik/ui';
import {
  getPaymentHistory,
  getPaymentHistoryDetail,
  type PaymentHistoryDetail,
  type PaymentHistoryItem,
  paymentReceiptUrl,
  manualAllocationEvidenceUrl,
} from '@urbanik/api-client';
import { apiBase } from './api-base';
import { openProtectedDocument } from './protected-document';
const labels: Record<string, string> = {
  submitted: 'Enviado',
  under_review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  applied: 'Aplicado',
  reversed: 'Reversado',
};
const money = (v: string) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(Number(v));
const eventLabels: Record<string, string> = {
  submitted: 'Comprobante registrado',
  approved: 'Comprobante aprobado',
  manual_allocation_saved: 'Distribución preparada',
  payment_applied: 'Pago aplicado',
  payment_reversed: 'Pago reversado',
  payment_evidence_deleted: 'Evidencia eliminada',
  payment_rejected: 'Comprobante rechazado',
};
const fmt = (v: string) =>
  new Intl.DateTimeFormat('es-EC', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'America/Guayaquil',
  }).format(new Date(v));
export function PaymentHistory() {
  const [q, setQ] = useState(''),
    [status, setStatus] = useState(''),
    [from, setFrom] = useState(''),
    [to, setTo] = useState(''),
    [page, setPage] = useState(1),
    [data, setData] = useState<{ items: PaymentHistoryItem[]; total: number; pageSize: number }>({
      items: [],
      total: 0,
      pageSize: 15,
    }),
    [detail, setDetail] = useState<PaymentHistoryDetail | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');
  useEffect(() => {
    setLoading(true);
    getPaymentHistory(apiBase, { query: q, status, from, to, page, pageSize: 15 })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q, status, from, to, page]);
  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  return (
    <section className="member-payment payment-history">
      <header>
        <div>
          <span>HISTORIAL AUDITADO</span>
          <h2>Historial de comprobantes</h2>
          <p>Consulta pagos, reversos, obligaciones y documentos asociados.</p>
        </div>
      </header>
      <div className="payment-history__filters">
        <label>
          <span>Buscar</span>
          <input
            aria-label="Buscar"
            placeholder="Socio o comprobante"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>Estado</span>
          <select
            aria-label="Estado"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos los estados</option>
            {Object.entries(labels).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Desde</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span>Hasta</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      {loading ? (
        <p>Cargando historial…</p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : !data.items.length ? (
        <p>No existen comprobantes para estos filtros.</p>
      ) : (
        <div className="payment-detail-modal__table payment-history__results">
          <table>
            <thead>
              <tr>
                <th>Socio</th>
                <th>Comprobante</th>
                <th>Registrado</th>
                <th>Valor</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((x) => (
                <tr key={x.id}>
                  <td data-label="Socio">
                    <a className="payment-history__member-link" href={'#socios/' + x.memberId}>
                      {x.memberName}
                    </a>
                  </td>
                  <td data-label="Comprobante">{x.reference}</td>
                  <td data-label="Registrado">{fmt(x.registeredAt)}</td>
                  <td data-label="Valor">{money(x.amount)}</td>
                  <td data-label="Estado">
                    {' '}
                    <StatusBadge
                      tone={
                        x.status === 'applied'
                          ? 'success'
                          : x.status === 'reversed' || x.status === 'rejected'
                            ? 'danger'
                            : 'warning'
                      }
                    >
                      {labels[x.status] ?? x.status}
                    </StatusBadge>
                  </td>
                  <td data-label="Acción">
                    {' '}
                    <button
                      className="ui-button ui-button--secondary"
                      onClick={() => getPaymentHistoryDetail(apiBase, x.id).then(setDetail)}
                    >
                      Ver detalle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <nav className="payment-history__pagination" aria-label="Paginación">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Anterior
        </button>
        <span>
          Página {page} de {pages} · {data.total} registros
        </span>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)}>
          Siguiente
        </button>
      </nav>
      <CanonicalModal
        open={Boolean(detail)}
        size="large"
        eyebrow="Trazabilidad"
        title={detail ? `Comprobante ${detail.payment.reference}` : 'Detalle'}
        description="Consulta qué ocurrió con el comprobante y cómo afectó cada obligación."
        onClose={() => setDetail(null)}
      >
        {detail ? (
          <>
            <dl className="payment-detail-modal__facts">
              <div>
                <dt>Socio</dt>
                <dd>
                  <a
                    className="payment-history__member-link"
                    href={'#socios/' + detail.payment.memberId}
                  >
                    {detail.payment.memberName}
                  </a>
                </dd>
              </div>
              <div>
                <dt>Estado</dt>
                <dd>{labels[detail.payment.status]}</dd>
              </div>
              <div>
                <dt>Valor</dt>
                <dd>{money(detail.payment.amount)}</dd>
              </div>
              <div>
                <dt>Registrado</dt>
                <dd>{fmt(detail.payment.registeredAt)}</dd>
              </div>
              {detail.payment.reversalReason ? (
                <div>
                  <dt>Motivo del reverso</dt>
                  <dd>{detail.payment.reversalReason}</dd>
                </div>
              ) : null}
            </dl>
            <div className="payment-history__section-heading">
              <div>
                <h3>Obligaciones afectadas</h3>
                <p>
                  El detalle ya se muestra aquí. Para revisar el estado de cuenta completo, abre la
                  ficha del socio.
                </p>
              </div>
              <a
                className="ui-button ui-button--secondary"
                href={'#socios/' + detail.payment.memberId}
              >
                Ver ficha del socio
              </a>
            </div>
            <div className="payment-detail-modal__table payment-history__applications">
              <table>
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Concepto</th>
                    <th>Identificador</th>
                    <th>Aplicado</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.applications.map((x) => (
                    <tr key={x.obligationId}>
                      <td data-label="Período">{x.period}</td>
                      <td data-label="Concepto">{x.conceptName}</td>
                      <td data-label="Identificador">
                        <code title={x.obligationId}>{x.obligationId.slice(0, 8)}</code>
                      </td>
                      <td data-label="Aplicado">{money(x.amount)}</td>
                      <td data-label="Estado">{x.reversed ? 'Reversado' : 'Aplicado'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3>Auditoría</h3>
            <ol>
              {detail.events.map((x, i) => (
                <li key={i}>
                  <strong>{eventLabels[x.event] ?? x.event}</strong>
                  <span>
                    {x.actor} · {fmt(x.at)}
                  </span>
                </li>
              ))}
            </ol>
            <div className="manual-allocation__actions">
              {detail.payment.evidenceAvailable ? (
                <button
                  type="button"
                  className="ui-button ui-button--secondary"
                  onClick={() => {
                    setError('');
                    void openProtectedDocument(
                      manualAllocationEvidenceUrl(apiBase, detail.payment.id),
                      'preview',
                    ).catch((failure) =>
                      setError(
                        failure instanceof Error
                          ? failure.message
                          : 'No se pudo abrir el documento.',
                      ),
                    );
                  }}
                >
                  Ver documento
                </button>
              ) : null}
              {detail.payment.status === 'applied' && detail.payment.receiptNumber ? (
                <button
                  type="button"
                  className="ui-button ui-button--primary"
                  onClick={() => {
                    setError('');
                    void openProtectedDocument(
                      paymentReceiptUrl(apiBase, detail.payment.id),
                      'download',
                      `${detail.payment.receiptNumber ?? 'recibo'}.png`,
                    ).catch((failure) =>
                      setError(
                        failure instanceof Error
                          ? failure.message
                          : 'No se pudo descargar el recibo.',
                      ),
                    );
                  }}
                >
                  Descargar recibo
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </CanonicalModal>
    </section>
  );
}
